/**
 * Centralized Braintree client SDK error constants
 *
 * These are the canonical `code` / `type` values on BraintreeError objects
 * returned by braintree-web (hosted fields tokenize, 3-D Secure verifyCard).
 * Display text is retrieved via translation keys.
 */

export const BraintreeErrorCode = {
  HOSTED_FIELDS_FIELDS_EMPTY: 'HOSTED_FIELDS_FIELDS_EMPTY',
  HOSTED_FIELDS_FIELDS_INVALID: 'HOSTED_FIELDS_FIELDS_INVALID',
  HOSTED_FIELDS_FAILED_TOKENIZATION: 'HOSTED_FIELDS_FAILED_TOKENIZATION',
  HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED: 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED',
  HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE: 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE',
  HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR: 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR',
} as const

export type BraintreeErrorCodeValue = typeof BraintreeErrorCode[keyof typeof BraintreeErrorCode]

export const BraintreeErrorType = {
  CUSTOMER: 'CUSTOMER',
  MERCHANT: 'MERCHANT',
  NETWORK: 'NETWORK',
  INTERNAL: 'INTERNAL',
  UNKNOWN: 'UNKNOWN',
} as const

export type BraintreeErrorTypeValue = typeof BraintreeErrorType[keyof typeof BraintreeErrorType]

/** All 3-D Secure SDK error codes share this prefix (THREEDS_LOOKUP_ERROR, THREEDS_AUTHENTICATION_IN_PROGRESS, ...) */
export const BRAINTREE_THREE_D_SECURE_CODE_PREFIX = 'THREEDS_'

export const PAYMENT_CREATION_UNKNOWN_ERROR_KEY = 'alerts.UnknownError'

/**
 * Get translation key for a Braintree client SDK error.
 * Falls back on the error type, then on the generic unknown error.
 */
export function getBraintreeErrorLabelKey(code: string, type: string): string {
  const codeMap: Record<string, string> = {
    [BraintreeErrorCode.HOSTED_FIELDS_FIELDS_EMPTY]: 'alerts.PaymentCreation.CardDetailsMissing',
    [BraintreeErrorCode.HOSTED_FIELDS_FIELDS_INVALID]: 'alerts.PaymentCreation.CardDetailsInvalid',
    [BraintreeErrorCode.HOSTED_FIELDS_FAILED_TOKENIZATION]: 'alerts.PaymentCreation.CardVerificationFailed',
    [BraintreeErrorCode.HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED]: 'alerts.PaymentCreation.CvvVerificationFailed',
    [BraintreeErrorCode.HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE]: 'alerts.PaymentCreation.CardAlreadySaved',
    [BraintreeErrorCode.HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR]: 'alerts.PaymentCreation.NetworkError',
  }
  if (codeMap[code]) {
    return codeMap[code]
  }
  if (code.startsWith(BRAINTREE_THREE_D_SECURE_CODE_PREFIX)) {
    return 'alerts.PaymentCreation.ThreeDSecureFailed'
  }

  const typeMap: Record<string, string> = {
    [BraintreeErrorType.CUSTOMER]: 'alerts.PaymentCreation.CardDetailsInvalid',
    [BraintreeErrorType.NETWORK]: 'alerts.PaymentCreation.NetworkError',
  }
  return typeMap[type] || PAYMENT_CREATION_UNKNOWN_ERROR_KEY
}
